import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { SelectedProposal } from 'entities/Proposal/model/types/ProposalSchema';

import { validateState } from '../lib/editOrderItemSourcingValidator';
import {
    EditOrderItemSourcingSchema,
    InitializeEditingPayload,
    OrderItemSourcingState,
    OrderItemSourcingValidationError,
    UpdateSupplierQuantityPayload,
    UpdateTotalQuantityPayload,
} from '../types/EditOrderItemSourcingSchema';

const initialState: EditOrderItemSourcingSchema = {
    originalState: null,
    currentState: null,
    proposals: [],
    isDirty: false,
    isLoading: false,
    isSourcingInProgress: false,
    validationErrors: [],
    itemId: null,
    orderId: null,
    availableQuantity: 0,
    vendorsMap: null,
    isPreSubmission: false,
};

export const EditOrderItemSourcingSlice = createSlice({
    name: 'EditOrderItemSourcing',
    initialState,
    reducers: {
        initializeEditing: (
            state,
            action: PayloadAction<InitializeEditingPayload>,
        ) => {
            const {
                itemId,
                orderId,
                totalQuantity,
                selectedProposals,
                proposals,
                isSourcingOverridden,
                availableQuantity,
                vendorsMap,
                isPreSubmission,
            } = action.payload;

            const originalState: OrderItemSourcingState = {
                totalQuantity,
                selectedProposals: [...selectedProposals],
                isSourcingOverridden,
            };

            const currentState: OrderItemSourcingState = {
                totalQuantity,
                selectedProposals: [...selectedProposals],
                isSourcingOverridden,
            };

            state.originalState = originalState;
            state.currentState = currentState;
            state.proposals = [...proposals];
            state.itemId = itemId;
            state.orderId = orderId;
            state.availableQuantity = availableQuantity;
            state.vendorsMap = vendorsMap || null;
            state.isDirty = false;
            state.validationErrors = [];
            state.isPreSubmission = isPreSubmission;

            // Validate and update errors
            state.validationErrors = validateState(
                state.currentState,
                state.availableQuantity,
                state.isPreSubmission,
                state.vendorsMap,
                state.proposals,
            );
        },

        updateTotalQuantity: (
            state,
            action: PayloadAction<UpdateTotalQuantityPayload>,
        ) => {
            if (!state.currentState) return;

            const { quantity } = action.payload;
            state.currentState.totalQuantity = quantity;
            state.isDirty = true;

            // Validate and update errors
            state.validationErrors = validateState(
                state.currentState,
                state.availableQuantity,
                state.isPreSubmission,
                state.vendorsMap,
                state.proposals,
            );
        },

        // This will be handled by async thunk in the service
        setTotalQuantityFromSourcing: (
            state,
            action: PayloadAction<{
                totalQuantity: number;
                selectedProposals: SelectedProposal[];
            }>,
        ) => {
            if (!state.currentState) return;

            const { totalQuantity, selectedProposals } = action.payload;
            state.currentState.totalQuantity = totalQuantity;
            state.currentState.selectedProposals = selectedProposals;

            state.isDirty = true;
            state.currentState.isSourcingOverridden = false; // API has reset sourcing, so it's no longer overridden

            state.validationErrors = validateState(
                state.currentState,
                state.availableQuantity,
                state.isPreSubmission,
                state.vendorsMap,
                state.proposals,
            );
        },

        updateSupplierQuantity: (
            state,
            action: PayloadAction<UpdateSupplierQuantityPayload>,
        ) => {
            if (!state.currentState) return;
            if (state.isSourcingInProgress) return;

            const { proposalUid, quantity } = action.payload;

            let proposal = state.currentState.selectedProposals.find(
                (p) => p.uid === proposalUid,
            );

            // If proposal doesn't exist, create it
            if (!proposal) {
                // Find the proposal in the proposals array to get name and price
                const sourceProposal = state.proposals.find(
                    (p) => p.uid === proposalUid,
                );

                if (!sourceProposal) {
                    console.warn(
                        `No proposal found for uid: ${proposalUid}`,
                        state.proposals,
                    );
                    return;
                }

                proposal = {
                    uid: proposalUid,
                    name: sourceProposal.seller.name,
                    price: sourceProposal.sellPrice ?? 0,
                    selectedQuantity: 0,
                };

                state.currentState.selectedProposals.push(proposal);
            }

            proposal.selectedQuantity = quantity;
            state.isDirty = true;
            state.currentState.isSourcingOverridden = true; // User has manually edited supplier quantities

            // Auto-update total to reflect sum of all supplier quantities
            const newTotal = state.currentState.selectedProposals.reduce(
                (sum, p) => sum + (p.selectedQuantity ?? 0),
                0,
            );
            state.currentState.totalQuantity = newTotal;

            // Validate and update errors
            state.validationErrors = validateState(
                state.currentState,
                state.availableQuantity,
                state.isPreSubmission,
                state.vendorsMap,
                state.proposals,
            );
        },

        resetToOriginal: (state) => {
            if (state.originalState) {
                state.currentState = {
                    totalQuantity: state.originalState.totalQuantity,
                    selectedProposals: [
                        ...state.originalState.selectedProposals,
                    ],
                    isSourcingOverridden:
                        state.originalState.isSourcingOverridden,
                };
                state.isDirty = false;
                state.validationErrors = [];
            }
        },

        setValidationErrors: (
            state,
            action: PayloadAction<OrderItemSourcingValidationError[]>,
        ) => {
            state.validationErrors = action.payload;
        },

        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },

        setSourcingInProgress: (state, action: PayloadAction<boolean>) => {
            state.isSourcingInProgress = action.payload;
        },

        cancelSourcing: (state) => {
            state.isSourcingInProgress = false;
        },

        clearState: (state) => {
            state.originalState = null;
            state.currentState = null;
            state.isDirty = false;
            state.isLoading = false;
            state.isSourcingInProgress = false;
            state.validationErrors = [];
            state.itemId = null;
            state.orderId = null;
            state.vendorsMap = null;
            state.isPreSubmission = false;
        },
    },
});

export const { actions: editOrderItemSourcingActions } =
    EditOrderItemSourcingSlice;
export const { reducer: editOrderItemSourcingReducer } =
    EditOrderItemSourcingSlice;
